import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, Sparkles, Save } from 'lucide-react';
import { ADMIN_AI_ENABLED } from '@/lib/featureFlags';

interface EditCopyDialogProps {
  campaign: {
    id: string;
    brand_name: string;
    slug?: string;
    placement: string;
    image_url: string | null;
    cta_url: string | null;
  };
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdated: () => void;
}

export default function EditCopyDialog({ campaign, open, onOpenChange, onUpdated }: EditCopyDialogProps) {
  // With AI off, skip the "Generate or write your own" chooser and drop
  // straight into the manual editor (prefilled with the brand name).
  const [headline, setHeadline] = useState(ADMIN_AI_ENABLED ? '' : campaign.brand_name);
  const [body, setBody] = useState('');
  const [ctaText, setCtaText] = useState(ADMIN_AI_ENABLED ? '' : 'Learn more');
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(!ADMIN_AI_ENABLED);

  const generateCopy = async () => {
    setGenerating(true);
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      // The placement enum (e.g. `passenger_home_header`) is not a
      // valid template_key. Map the most common placements to the
      // matching sponsor_templates row so the AI generates copy that
      // fits the placement style. Unknown placements fall back to
      // `hotel_monthly` which is a generic, well-tuned prompt.
      const placementToTemplate: Record<string, string> = {
        passenger_home_header: 'hotel_monthly',
        commute_morning_takeover: 'telecom_commute',
        commute_evening_takeover: 'telecom_commute',
        ride_complete_full: 'restaurant_evening',
        weather_alert_banner: 'ferry_weather',
        map_pin_featured: 'shop_local_pin',
        festival_event_takeover: 'festival_takeover',
        splash_takeover: 'launch_splash',
      };
      const templateKey = placementToTemplate[campaign.placement] || 'hotel_monthly';
      const res = await fetch(`${supabaseUrl}/functions/v1/generate-sponsor-creative`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${anonKey}`,
        },
        body: JSON.stringify({
          brand_name: campaign.brand_name,
          brand_description: '',
          template_key: templateKey,
        }),
      });
      if (!res.ok) throw new Error('Edge Function error');
      const json = await res.json();
      setHeadline(json.headline || campaign.brand_name);
      setBody(json.body || '');
      setCtaText(json.cta_text || 'Learn more');
      setHasGenerated(true);
      if (json.ai_used === false) {
        toast.info('The AI copywriter is offline. Feel free to write your own copy.');
      }
    } catch {
      toast.error('Failed to generate copy. Write your own below.');
      if (!hasGenerated) {
        setHeadline(campaign.brand_name);
        setBody('');
        setCtaText('Learn more');
        setHasGenerated(true);
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!headline.trim()) {
      toast.error('Headline is required');
      return;
    }
    setSaving(true);
    try {
      const { data, error } = await supabase.rpc('update_campaign_creative_from_ai', {
        p_campaign_id: campaign.id,
        p_headline: headline,
        p_body: body,
        p_cta_text: ctaText,
      });
      if (error) throw error;
      if (data?.ok === false) throw new Error(data.error || 'Update failed');
      toast.success(`Updated copy for ${campaign.brand_name}`);
      onOpenChange(false);
      onUpdated();
    } catch (err: any) {
      toast.error(err.message || 'Failed to update copy');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-gray-950 border-gray-800 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-emerald-400" />
            Edit copy: {campaign.brand_name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {!hasGenerated ? (
            <div className="text-center py-8">
              <p className="text-sm text-gray-400 mb-4">Generate fresh AI copy or write your own.</p>
              <div className="flex justify-center gap-3">
                <Button
                  onClick={generateCopy}
                  disabled={generating}
                  className="bg-emerald-600 hover:bg-emerald-500 text-white"
                >
                  {generating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
                  Generate with AI
                </Button>
                <Button
                  variant="outline"
                  className="border-gray-700 text-gray-300"
                  onClick={() => {
                    setHeadline(campaign.brand_name);
                    setBody('');
                    setCtaText('Learn more');
                    setHasGenerated(true);
                  }}
                >
                  Write my own
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-gray-300">Headline</Label>
                  <span className={`text-[10px] tabular-nums ${headline.length > 32 ? 'text-red-400' : 'text-gray-500'}`}>
                    {headline.length}/32
                  </span>
                </div>
                <Input
                  value={headline}
                  onChange={(e) => { if (e.target.value.length <= 32) setHeadline(e.target.value); }}
                  className="bg-gray-900 border-gray-800 text-white font-semibold"
                  placeholder="Headline"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-gray-300">Body</Label>
                  <span className={`text-[10px] tabular-nums ${body.length > 110 ? 'text-red-400' : 'text-gray-500'}`}>
                    {body.length}/110
                  </span>
                </div>
                <Textarea
                  value={body}
                  onChange={(e) => { if (e.target.value.length <= 110) setBody(e.target.value); }}
                  className="bg-gray-900 border-gray-800 text-white min-h-[60px] resize-none"
                  placeholder="Ad body text"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-gray-300">CTA text</Label>
                  <span className={`text-[10px] tabular-nums ${ctaText.length > 14 ? 'text-red-400' : 'text-gray-500'}`}>
                    {ctaText.length}/14
                  </span>
                </div>
                <Input
                  value={ctaText}
                  onChange={(e) => { if (e.target.value.length <= 14) setCtaText(e.target.value); }}
                  className="bg-gray-900 border-gray-800 text-white w-40"
                  placeholder="See rates"
                />
              </div>

              {ADMIN_AI_ENABLED && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={generateCopy}
                  disabled={generating}
                  className="text-gray-400 hover:text-emerald-400"
                >
                  {generating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Sparkles className="w-4 h-4 mr-1" />}
                  Generate again
                </Button>
              )}
            </>
          )}
        </div>

        {hasGenerated && (
          <DialogFooter>
            <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-gray-400">
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || !headline.trim()}
              className="bg-emerald-600 hover:bg-emerald-500 text-white"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
